import { Flex, Squish } from '@grubhubprod/gh-cookbook-react/dist';
import get from 'lodash/get';
import React, { ReactNode } from 'react';
import {
  FormInput,
  Label,
  IFormInputProps,
  DropdownProps,
  Dropdown,
  DropdownList,
  DropdownSection,
  DropdownItem,
} from '@grubhubprod/gh-cookbook-react';
import { Control, RegisterOptions, useController } from 'react-hook-form';
import { SearchDropdownSection } from 'src/components/CookbookSingleSelectSearchBase/SearchDropdownSection';
import { DropdownOptionProps } from 'src/components/CookbookSelectWrapper/CookbookSelectWrapper';

export type CookbookSingleSelectSearchWrapperBaseProps = {
  name: string;
  id: string;
  control: Control;
  options: DropdownOptionProps[];
  rules?: RegisterOptions;
  label?: ReactNode;
  ariaLabel?: string;
  maxHeight?: string;
};

export type CookbookSingleSelectSearchWrapperProps = Omit<
  DropdownProps,
  'value' | 'onSelect' | 'error' | 'label'
> &
  CookbookSingleSelectSearchWrapperBaseProps &
  Pick<IFormInputProps, 'inputSize' | 'blockWidth' | 'error'> & {
    onCollapse?: () => void;
    disableKeyDownOverride?: boolean;
    onSelect?: (value: string) => void;
    onSearchChange: (value: string) => void;
  };

export function CookbookSingleSelectSearchBase({
  name,
  id,
  control,
  rules = {},
  error,
  ariaLabel,
  label = '',
  maxHeight = '300px',
  inputSize = 'medium',
  blockWidth,
  disabled,
  options,
  onCollapse,
  onSelect,
  onSearchChange,
  disableKeyDownOverride = true,
  ...cookbookDropdownProps
}: CookbookSingleSelectSearchWrapperProps): JSX.Element {
  const { field } = useController({
    name,
    rules,
    control,
    defaultValue: get(control.defaultValuesRef.current, name) ?? '',
  });

  return (
    <FormInput inputSize={inputSize} blockWidth={blockWidth} error={error}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Flex flexDirection="column">
        <Dropdown
          {...cookbookDropdownProps}
          disabled={disabled}
          aria-label={label}
          multiple={false}
          block={blockWidth}
          error={!!error}
          id={id}
          value={field.value}
          onSelect={(value: string): void => {
            onSelect?.(value);
            field.onChange(value);
          }}
          {...(ariaLabel && { 'aria-label': ariaLabel })}
          {...(disableKeyDownOverride && { disableKeyDownOverride })}
          {...(onCollapse && { onCollapse })}
        >
          <DropdownList maxHeight={maxHeight}>
            <DropdownSection
              customComponent={
                <SearchDropdownSection onSearchChange={onSearchChange} />
              }
            />
            {options.length > 0 && (
              <DropdownSection key="options-section">
                {options.map((option) => (
                  <DropdownItem
                    key={`${option.value}${option.text}`}
                    text={option.text}
                    value={option.value}
                  />
                ))}
              </DropdownSection>
            )}
            {options.length === 0 && (
              <DropdownSection
                customComponent={
                  <Squish inset="lg">
                    <Flex justifyContent="center" alignItems="center">
                      No results found.
                    </Flex>
                  </Squish>
                }
                key="no-options-section"
              />
            )}
          </DropdownList>
        </Dropdown>
      </Flex>
    </FormInput>
  );
}
